import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Users, Sparkles, Loader2, LayoutGrid, Table2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FieldHint } from '@/components/field-hint'
import { Skeleton } from '@/components/ui/skeleton'
import { SearchResultCard } from './search-result-card'
import { SearchResultsTable } from './search-results-table'
import type { LinkedInProfile } from './use-search'

type ViewMode = 'cards' | 'table'
const VIEW_MODE_STORAGE_KEY = 'leadDiscovery.viewMode'

// --- Loading skeleton ---

function ResultSkeleton() {
  return (
    <div className="rounded-xl border bg-card p-4 shadow">
      <div className="flex items-start gap-3">
        <Skeleton className="mt-1 h-4 w-4 rounded" />
        <Skeleton className="h-10 w-10 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-8 w-16 rounded-md" />
      </div>
    </div>
  )
}

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

// --- Props ---

type SearchResultsProps = {
  readonly results: readonly LinkedInProfile[]
  readonly _total?: number
  readonly isLoading: boolean
  readonly hasSearched: boolean
  readonly selectedIds: ReadonlySet<string>
  readonly enrichingIds: ReadonlySet<string>
  readonly onSelect: (id: string, selected: boolean) => void
  /**
   * Toggle selection of a specific set of IDs. The child computes the
   * page-scoped importable IDs and passes them so the parent doesn't need
   * to know about pagination. selected=true means add to selection;
   * selected=false means remove. See lead-discovery-page handleSelectAll.
   */
  readonly onSelectAll: (ids: readonly string[], selected: boolean) => void
  readonly onImport: (profile: LinkedInProfile) => void
  readonly onEnrich: (profile: LinkedInProfile) => void
  readonly onBulkImport: () => void
  readonly onBulkEnrich: () => void
  readonly onLoadMore: () => void
  readonly hasNextPage: boolean
}

export function SearchResults({
  results,
  isLoading,
  hasSearched,
  selectedIds,
  enrichingIds,
  onSelect,
  onSelectAll,
  onImport,
  onEnrich,
  onBulkImport,
  onBulkEnrich,
  onLoadMore,
  hasNextPage,
}: SearchResultsProps) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<number>(20)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'cards'
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    return stored === 'table' ? 'table' : 'cards'
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode)
  }, [viewMode])

  // Reset page when results change
  const totalResults = results.length
  const totalPages = Math.max(1, Math.ceil(totalResults / pageSize))
  const safePage = Math.min(page, totalPages - 1)
  if (safePage !== page) setPage(safePage)

  const startIdx = safePage * pageSize
  const endIdx = Math.min(startIdx + pageSize, totalResults)
  const pageResults = results.slice(startIdx, endIdx)

  const importableResults = pageResults.filter((r) => !r.in_pipeline)
  const allImportableSelected =
    importableResults.length > 0 &&
    importableResults.every((r) => selectedIds.has(r.id))

  // Not yet searched
  if (!hasSearched && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-muted/30 py-16 text-center">
        <Users className="h-10 w-10 text-muted-foreground/50" />
        <div>
          <p className="font-medium text-muted-foreground">Find your ideal leads</p>
          <p className="mt-1 text-sm text-muted-foreground/70">
            Use the filters on the left to search LinkedIn profiles.
          </p>
        </div>
      </div>
    )
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <ResultSkeleton key={i} />
          ))}
        </div>
      </div>
    )
  }

  // No results
  if (hasSearched && results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-muted/30 py-16 text-center">
        <Users className="h-10 w-10 text-muted-foreground/50" />
        <div>
          <p className="font-medium text-muted-foreground">No results found</p>
          <p className="mt-1 text-sm text-muted-foreground/70">
            Try adjusting your filters or using different keywords.
          </p>
        </div>
      </div>
    )
  }

  const selectedCount = selectedIds.size
  // Decide which bulk action to surface based on what's selected. Mixed
  // selections favor the "more advanced action needed first" path (enrich
  // before import) since previews can't be imported anyway.
  const selectedProfiles = pageResults.filter((p) => selectedIds.has(p.id))
  const selectedPreviewCount = selectedProfiles.filter(
    (p) => !p.is_enriched && p.apollo_id,
  ).length
  const selectedEnrichedCount = selectedProfiles.filter((p) => p.is_enriched).length
  const anyEnrichingSelected = selectedProfiles.some((p) => enrichingIds.has(p.id))

  return (
    <div className="flex flex-col gap-3" data-tour="discover-results">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          {/* Card view keeps its own select-all checkbox up here; the table
              renders one in its header instead. */}
          {viewMode === 'cards' && importableResults.length > 0 && (
            <input
              type="checkbox"
              checked={allImportableSelected}
              onChange={(e) => onSelectAll(importableResults.map((r) => r.id), e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border-[rgba(255,255,255,0.08)] text-primary focus:ring-primary"
              aria-label="Select all on this page"
            />
          )}
          <span className="text-sm text-muted-foreground">
            Showing {startIdx + 1} to {endIdx} of {totalResults} results
          </span>
          <FieldHint label="How do results work?">
            Provider results start as previews. Enrich a preview for one credit to unlock it. Enriched leads are saved to your Leads page automatically. Select all covers this page only.
          </FieldHint>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* View toggle: Cards vs Table */}
          <div className="flex items-center gap-0.5 rounded-md border bg-background p-0.5" data-tour="discover-view-toggle">
            <Button
              size="sm"
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              onClick={() => setViewMode('cards')}
              className="h-7 gap-1 px-2 text-xs"
              aria-pressed={viewMode === 'cards'}
            >
              <LayoutGrid className="h-3 w-3" />
              <span className="hidden sm:inline">Cards</span>
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              onClick={() => setViewMode('table')}
              className="h-7 gap-1 px-2 text-xs"
              aria-pressed={viewMode === 'table'}
            >
              <Table2 className="h-3 w-3" />
              <span className="hidden sm:inline">Table</span>
            </Button>
          </div>

          {/* Page size selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}
              className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar — enrich previews first, then import enriched */}
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5">
          <span className="text-sm font-medium">
            {selectedCount} lead{selectedCount !== 1 ? 's' : ''} selected
            {selectedPreviewCount > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({selectedPreviewCount} preview{selectedPreviewCount !== 1 ? 's' : ''}
                {selectedEnrichedCount > 0
                  ? `, ${selectedEnrichedCount} enriched`
                  : ''})
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            {selectedPreviewCount > 0 && (
              <Button
                size="sm"
                onClick={onBulkEnrich}
                disabled={anyEnrichingSelected}
                className="gap-1"
              >
                {anyEnrichingSelected ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Enriching…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3" />
                    Enrich {selectedPreviewCount} lead{selectedPreviewCount !== 1 ? 's' : ''}
                    {' '}({selectedPreviewCount} credit{selectedPreviewCount !== 1 ? 's' : ''})
                  </>
                )}
              </Button>
            )}
            {selectedEnrichedCount > 0 && (
              <Button size="sm" onClick={onBulkImport}>
                Import {selectedEnrichedCount} lead{selectedEnrichedCount !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Results — cards or table depending on viewMode */}
      {viewMode === 'cards' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {pageResults.map((profile) => (
            <SearchResultCard
              key={profile.id}
              profile={profile}
              isSelected={selectedIds.has(profile.id)}
              isEnriching={enrichingIds.has(profile.id)}
              onSelect={onSelect}
              onImport={onImport}
              onEnrich={onEnrich}
            />
          ))}
        </div>
      ) : (
        <SearchResultsTable
          results={pageResults}
          selectedIds={selectedIds}
          enrichingIds={enrichingIds}
          onSelect={onSelect}
          onSelectAll={onSelectAll}
          onImport={onImport}
          onEnrich={onEnrich}
        />
      )}

      {/* Pagination controls */}
      <div className="flex items-center justify-between border-t pt-3">
        <Button
          variant="outline"
          size="sm"
          disabled={safePage === 0}
          onClick={() => setPage((p) => p - 1)}
          className="gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        <span className="text-sm text-muted-foreground">
          Page {safePage + 1} of {totalPages}
        </span>

        <Button
          variant="outline"
          size="sm"
          disabled={safePage >= totalPages - 1 && !hasNextPage}
          onClick={() => {
            if (safePage < totalPages - 1) {
              setPage((p) => p + 1)
            } else if (hasNextPage) {
              onLoadMore()
            }
          }}
          className="gap-1"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
